import { useMemo, useState } from 'react';
import {
  Loader2,
  MapPin,
  Minus,
  MousePointerClick,
  Pencil,
  Plus,
  Search,
  Trash2
} from 'lucide-react';
import type {
  CreateStorageLocationPayload,
  StorageLocationNode
} from '../Types';
import { cn } from '@/shared/utils/helpers';
import { Skeleton } from '@/shared/components/ui';
import {
  findStorageLocationNode,
  formatStorageLocationPath
} from '../Utils/StorageLocation.utils';
import {
  onboardingDualPanelGridClass,
  onboardingStepShellClass
} from '../Utils/onboardingLayout';
import { AddStorageLocationForm } from './AddStorageLocationForm';
import type { ListedInventoryItem } from './AddItemsForm';
import { StorageTree } from './StorageTree';

const checkboxClass =
  'h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-2 border-[#b9bfc7] bg-white transition hover:border-[#087f8c] checked:border-[#087f8c] checked:bg-[#087f8c] indeterminate:border-[#087f8c] indeterminate:bg-[#087f8c]/30 disabled:cursor-not-allowed disabled:opacity-40';

const getDisplayStockUnit = (unit: string): string => {
  const trimmed = unit.trim();
  return /^val(?:ue)?(?:\s|$)/i.test(trimmed) ? '' : trimmed;
};

interface StorageStepProps {
  items: ListedInventoryItem[];
  locations: StorageLocationNode[];
  defaultLocationId: string | null;
  locationsLoading?: boolean;
  creatingLocation?: boolean;
  onCreateLocation?: (
    payload: CreateStorageLocationPayload
  ) => Promise<StorageLocationNode>;
  onBulkAssignLocation: (
    items: ListedInventoryItem[],
    locationId: string
  ) => Promise<void>;
  onNext: () => void | Promise<void>;
  onPrev: () => void;
}

export const StorageStep = ({
  items,
  locations,
  defaultLocationId,
  locationsLoading = false,
  creatingLocation = false,
  onCreateLocation,
  onBulkAssignLocation,
  onNext,
  onPrev
}: StorageStepProps) => {
  const [filter, setFilter] = useState<'all' | 'unassigned'>('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [categoryKey, setCategoryKey] = useState('');
  const [targetLocationId, setTargetLocationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [addTarget, setAddTarget] = useState<string | null>(null);

  const itemIsUnassigned = (item: ListedInventoryItem) => {
    return !item.locationId || item.locationId === defaultLocationId;
  };

  const unassignedCount = useMemo(
    () => items.filter(itemIsUnassigned).length,
    [defaultLocationId, items]
  );

  const itemCategoryKey = (item: ListedInventoryItem) =>
    (item.categoryName?.trim() || 'Uncategorized').toLowerCase();

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items
      .filter(item => {
        if (query && !item.name.toLowerCase().includes(query)) return false;
        if (filter === 'unassigned') return itemIsUnassigned(item);
        return true;
      })
      .sort((a, b) => {
        // Category pick from dropdown: those items float to the top.
        if (categoryKey) {
          const aInCategory = itemCategoryKey(a) === categoryKey ? 0 : 1;
          const bInCategory = itemCategoryKey(b) === categoryKey ? 0 : 1;
          if (aInCategory !== bInCategory) return aInCategory - bInCategory;
        }
        // Unassigned first so the work queue is visible immediately.
        const aUnassigned = itemIsUnassigned(a) ? 0 : 1;
        const bUnassigned = itemIsUnassigned(b) ? 0 : 1;
        if (aUnassigned !== bUnassigned) return aUnassigned - bUnassigned;
        return a.name.localeCompare(b.name);
      });
  }, [categoryKey, defaultLocationId, filter, items, search]);

  const categoryOptions = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    items.forEach(item => {
      const label = item.categoryName?.trim() || 'Uncategorized';
      const key = label.toLowerCase();
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { label, count: 1 });
    });
    return Array.from(counts.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => {
        if (a.label === 'Uncategorized') return 1;
        if (b.label === 'Uncategorized') return -1;
        return a.label.localeCompare(b.label);
      });
  }, [items]);

  const selectedItems = useMemo(
    () => items.filter(item => selectedIds.has(item.localId)),
    [items, selectedIds]
  );

  const targetLocationName = useMemo(() => {
    if (!targetLocationId) return null;
    return findStorageLocationNode(locations, targetLocationId)?.name ?? null;
  }, [locations, targetLocationId]);

  const allSelected =
    filteredItems.length > 0 &&
    filteredItems.every(item => selectedIds.has(item.localId));

  const toggleItem = (localId: string) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (next.has(localId)) next.delete(localId);
      else next.add(localId);
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setCategoryKey('');
    setSelectedIds(previous => {
      const next = new Set(previous);
      filteredItems.forEach(item => {
        if (checked) next.add(item.localId);
        else next.delete(item.localId);
      });
      return next;
    });
  };

  const selectCategory = (categoryKey: string) => {
    setCategoryKey(categoryKey);
    if (!categoryKey) return;
    const next = new Set<string>();
    items.forEach(item => {
      const label = item.categoryName?.trim() || 'Uncategorized';
      if (label.toLowerCase() === categoryKey) next.add(item.localId);
    });
    setSelectedIds(next);
  };

  const toggleCollapse = (id: string) => {
    setCollapsedIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openAddForm = (parentId: string) => {
    setAddTarget(parentId);
    setCollapsedIds(previous => {
      if (!previous.has(parentId)) return previous;
      const next = new Set(previous);
      next.delete(parentId);
      return next;
    });
  };

  const selectTargetLocation = (node: StorageLocationNode) => {
    setTargetLocationId(previous => (previous === node.id ? null : node.id));
  };

  const applyLocation = async () => {
    if (!targetLocationId || selectedItems.length === 0) return;
    setBusy(true);
    try {
      await onBulkAssignLocation(selectedItems, targetLocationId);
      setSelectedIds(new Set());
      setCategoryKey('');
      setTargetLocationId(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={onboardingStepShellClass}>
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
          Storage
        </h2>
        <p className="mt-1 text-sm text-[#6f747c]">
          Create rooms and positions, then assign items to them.
        </p>
      </div>

      <div className={onboardingDualPanelGridClass}>
        {/* Left — same column width / top spacing as Items step list */}
        <section className="order-2 flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-[#e7e9ee] bg-white shadow-sm lg:order-1 lg:h-full">
          <div className="shrink-0 border-b border-[#e7e9ee] px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-[#171717]">
                Your items
              </h3>
              <span className="text-[11px] text-[#8a8f96]">
                {unassignedCount > 0
                  ? `${unassignedCount} unassigned`
                  : 'All assigned'}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-[#8a8f96]">
              {selectedItems.length > 0
                ? `${selectedItems.length} selected — pick a location on the right`
                : 'Select items, or pick a category below'}
            </p>
          </div>

          <div className="shrink-0 space-y-2 border-b border-[#e7e9ee] p-3">
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  { id: 'all', label: 'All', count: items.length },
                  {
                    id: 'unassigned',
                    label: 'Unassigned',
                    count: unassignedCount
                  }
                ] as const
              ).map(itemFilter => (
                <button
                  key={itemFilter.id}
                  type="button"
                  onClick={() => setFilter(itemFilter.id)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition',
                    filter === itemFilter.id
                      ? 'border-[#087f8c] bg-[#087f8c] text-white'
                      : 'border-[#e1e4e8] bg-white text-[#5f646b] hover:border-[#087f8c]/50'
                  )}
                >
                  {itemFilter.label} ({itemFilter.count})
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#8a8f96]" />
              <input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search items"
                className="h-8 w-full rounded-md border border-[#e7e9ee] bg-white pl-8 pr-3 text-xs outline-none focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/15"
              />
            </div>
            {categoryOptions.length > 0 && (
              <div className="space-y-1">
                <p className="text-[11px] font-semibold text-[#5f646b]">
                  Assign whole category
                </p>
                <select
                  value={categoryKey}
                  disabled={busy}
                  onChange={event => selectCategory(event.target.value)}
                  className="h-9 w-full rounded-md border border-[#e7e9ee] bg-white px-2 text-xs text-[#171717] outline-none focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/15 disabled:opacity-50"
                >
                  <option value="">Choose category…</option>
                  {categoryOptions.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label} ({option.count})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="min-h-0 max-h-[min(68vh,620px)] flex-1 space-y-2 overflow-y-auto bg-[#f7f8f9] p-3 lg:max-h-none">
            {items.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#dfe3e7] bg-white px-4 py-8 text-center text-sm text-[#6f747c]">
                No items yet. Go back and add some first.
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#dfe3e7] bg-white px-4 py-8 text-center text-sm text-[#6f747c]">
                {search.trim()
                  ? 'No items match your search.'
                  : 'No unassigned items.'}
              </div>
            ) : (
              <>
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#e7e9ee] bg-white px-3 py-2 text-[11px] font-semibold text-[#5f646b]">
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={allSelected}
                    disabled={busy}
                    ref={element => {
                      if (element) {
                        element.indeterminate =
                          selectedItems.length > 0 && !allSelected;
                      }
                    }}
                    onChange={event => toggleSelectAll(event.target.checked)}
                  />
                  Select all ({filteredItems.length})
                </label>

                {filteredItems.map(item => {
                  const isSelected = selectedIds.has(item.localId);
                  const unassigned = itemIsUnassigned(item);
                  const quantity = Math.max(
                    0,
                    Number(item.quantityOnHand ?? 0)
                  );
                  const displayStockUnit = getDisplayStockUnit(item.stockUnit);
                  const locationLeaf = item.locationId
                    ? (findStorageLocationNode(locations, item.locationId)
                        ?.name ??
                      formatStorageLocationPath(locations, item.locationId))
                    : null;

                  return (
                    <article
                      key={item.localId}
                      className={cn(
                        'rounded-lg border bg-white p-3 shadow-sm transition',
                        isSelected
                          ? 'border-[#087f8c]/60 bg-[#087f8c]/[0.04]'
                          : 'border-[#e7e9ee]'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className={checkboxClass}
                          checked={isSelected}
                          disabled={busy}
                          onChange={() => {
                            setCategoryKey('');
                            toggleItem(item.localId);
                          }}
                          aria-label={`Select ${item.name}`}
                        />

                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-sm font-semibold text-[#171717]">
                            {item.name}
                          </h3>
                          <p className="mt-0.5 text-[11px] text-[#6f747c]">
                            Min: {item.parLevel ?? 0}
                            {displayStockUnit ? ` ${displayStockUnit}` : ''}
                          </p>
                          <span
                            className={cn(
                              'mt-1 inline-flex flex-wrap items-center gap-1.5 text-[11px] font-semibold',
                              unassigned ? 'text-[#d16b68]' : 'text-[#087f8c]'
                            )}
                          >
                            <MapPin className="h-3 w-3" />
                            {unassigned ? 'Unassigned' : locationLeaf || 'Assigned'}
                          </span>
                        </div>

                        <div className="flex shrink-0 items-center gap-1">
                          <span
                            title="Edit quantity in Items step"
                            className="inline-flex"
                          >
                            <button
                              type="button"
                              disabled
                              className="pointer-events-none grid h-8 w-8 place-items-center rounded-md bg-[#087f8c] text-white opacity-40"
                              aria-label={`Decrease ${item.name}`}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                          </span>
                          <div
                            title={`${quantity}${displayStockUnit ? ` ${displayStockUnit}` : ''}`}
                            className="flex h-8 w-[78px] shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border border-[#dfe3e7] bg-white px-1.5 text-xs font-bold tabular-nums text-[#087f8c]"
                          >
                            <span className="shrink-0">{quantity}</span>
                            {displayStockUnit && (
                              <span className="min-w-0 truncate font-medium text-[#6f747c]">
                                {displayStockUnit}
                              </span>
                            )}
                          </div>
                          <span
                            title="Edit quantity in Items step"
                            className="inline-flex"
                          >
                            <button
                              type="button"
                              disabled
                              className="pointer-events-none grid h-8 w-8 place-items-center rounded-md bg-[#087f8c] text-white opacity-40"
                              aria-label={`Increase ${item.name}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </span>
                          <div className="ml-0.5 flex flex-col gap-1">
                            <span
                              title="Edit in Items step"
                              className="inline-flex"
                            >
                              <button
                                type="button"
                                disabled
                                className="pointer-events-none grid h-7 w-7 place-items-center rounded-md border border-[#e7e9ee] bg-white text-[#9a9fa6] opacity-40"
                                aria-label={`Edit ${item.name}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </span>
                            <span
                              title="Remove in Items step"
                              className="inline-flex"
                            >
                              <button
                                type="button"
                                disabled
                                className="pointer-events-none grid h-7 w-7 place-items-center rounded-md border border-[#e7e9ee] bg-white text-[#9a9fa6] opacity-40"
                                aria-label={`Remove ${item.name}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </>
            )}
          </div>
        </section>

        {/* Right — storage layout */}
        <section className="order-1 flex min-h-0 flex-col space-y-3 overflow-hidden rounded-lg border border-[#e7e9ee] bg-white p-5 shadow-sm lg:order-2 lg:h-full">
          <div className="flex shrink-0 items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-[#171717]">
                Storage layout
              </h3>
              <p className="mt-0.5 text-[11px] text-[#8a8f96]">
                Rooms → sections → shelves → bins
              </p>
            </div>
            {onCreateLocation && (
              <button
                type="button"
                onClick={() => setAddTarget('root')}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#087f8c] px-2.5 text-xs font-bold text-white transition hover:bg-[#066873]"
              >
                <Plus className="h-3.5 w-3.5" />
                Add room
              </button>
            )}
          </div>

          {selectedItems.length > 0 ? (
            <div className="flex shrink-0 items-start gap-2 rounded-md border border-[#087f8c]/25 bg-[#087f8c]/[0.06] px-3 py-2">
              <MousePointerClick className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#087f8c]" />
              <p className="text-[11px] leading-relaxed text-[#087f8c]">
                <span className="font-bold">
                  {selectedItems.length} item
                  {selectedItems.length === 1 ? '' : 's'} selected.
                </span>{' '}
                Hover any location, check the box on the left, then assign
                below.
              </p>
            </div>
          ) : (
            <div className="flex shrink-0 items-start gap-2 rounded-md border border-[#e7e9ee] bg-[#fafbfc] px-3 py-2">
              <MousePointerClick className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#8a8f96]" />
              <p className="text-[11px] leading-relaxed text-[#6f747c]">
                <span className="font-semibold text-[#5f646b]">
                  How to assign:
                </span>{' '}
                select items on the left → check a location here → press Assign.
              </p>
            </div>
          )}

          {addTarget === 'root' && onCreateLocation && (
            <div className="shrink-0">
              <AddStorageLocationForm
                inline
                parent={null}
                submitting={creatingLocation}
                onCancel={() => setAddTarget(null)}
                onCreate={onCreateLocation}
                onCreated={() => setAddTarget(null)}
              />
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {locationsLoading ? (
              <div className="space-y-1 rounded-md border border-[#e7e9ee] bg-[#fafbfc] p-2">
                {[0, 1, 2, 3, 4].map(row => (
                  <div
                    key={row}
                    className="flex items-center gap-2 rounded-md px-2 py-2"
                    style={{ paddingLeft: 8 + (row % 3) * 18 }}
                  >
                    <Skeleton className="h-4 w-4 shrink-0 rounded bg-[#e7e9ee]" />
                    <Skeleton className="h-3.5 min-w-0 flex-1 rounded bg-[#e7e9ee]" />
                    <Skeleton className="h-4 w-12 shrink-0 rounded-full bg-[#e7e9ee]" />
                    <Skeleton className="h-3 w-10 shrink-0 rounded bg-[#e7e9ee]" />
                  </div>
                ))}
              </div>
            ) : locations.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#dfe3e7] px-4 py-8 text-center">
                <p className="text-sm font-semibold text-[#171717]">
                  No storage positions yet
                </p>
                <p className="mt-1 text-xs text-[#6f747c]">
                  Add a room such as “Dry Store” or “Walk-in Fridge”.
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-[#e7e9ee] bg-[#fafbfc] p-1.5">
                <StorageTree
                  nodes={locations}
                  collapsedIds={collapsedIds}
                  addParentId={addTarget === 'root' ? null : addTarget}
                  creatingLocation={creatingLocation}
                  selectedLocationId={targetLocationId}
                  selectionEnabled={selectedItems.length > 0}
                  onSelectLocation={selectTargetLocation}
                  onToggleCollapse={toggleCollapse}
                  onRequestAdd={node => openAddForm(node.id)}
                  onCancelAdd={() => setAddTarget(null)}
                  onCreated={() => setAddTarget(null)}
                  onCreateLocation={onCreateLocation}
                />
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-[#e7e9ee] pt-3">
            <button
              type="button"
              disabled={!targetLocationId || selectedItems.length === 0 || busy}
              onClick={() => void applyLocation()}
              className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#087f8c] px-3 text-sm font-bold text-white transition hover:bg-[#066873] disabled:cursor-not-allowed disabled:opacity-45"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : selectedItems.length === 0 ? (
                'Select items on the left first'
              ) : !targetLocationId ? (
                'Check a location above'
              ) : (
                <>
                  Assign {selectedItems.length} item
                  {selectedItems.length === 1 ? '' : 's'} to{' '}
                  <span className="font-semibold opacity-90">
                    {targetLocationName}
                  </span>
                </>
              )}
            </button>
          </div>
        </section>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-[#e7e9ee] pt-4">
        <button
          type="button"
          onClick={onPrev}
          disabled={busy}
          className="rounded-md px-3 py-2 text-sm font-medium text-[#6f747c] transition hover:bg-[#f0f2f5] hover:text-[#171717] disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void onNext()}
          className="ml-auto rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#066873] disabled:opacity-50"
        >
          Continue to suppliers
        </button>
      </div>
    </div>
  );
};
